import { Injectable } from '@angular/core';

/**
 * Takes a picture of what the user is currently looking at.
 *
 * Uses the browser's own screen-capture API, which photographs the rendered
 * surface — so the screenshot shows the actual bug, including anything a DOM
 * re-rendering library would have drawn differently (custom fonts, canvas
 * content, native form controls). The trade-off is reach: the API does not
 * exist on iOS Safari or most mobile browsers, which is why {@link isSupported}
 * is public and the Report dialog offers a file picker instead when it is
 * false. Capture is never required to file a report.
 *
 * The user must pick a surface and approve the share — by design, and worth
 * keeping: the page cannot silently photograph someone's screen.
 */
@Injectable({ providedIn: 'root' })
export class ScreenCaptureService {
  /**
   * Whether this browser can capture the screen at all.
   *
   * Checked rather than attempted, because calling an absent API throws and a
   * failed capture is indistinguishable from a user declining the prompt.
   */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  }

  /**
   * Capture one frame of a surface the user chooses.
   *
   * Resolves `null` when the user cancels the picker — a cancel is an ordinary
   * choice, not an error, and must leave the report they were writing intact.
   * Rejects only on a genuine capture failure.
   */
  async capture(): Promise<Blob | null> {
    if (!this.isSupported()) return null;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        // Ask for the current tab first: it is the one with the bug in it, and
        // it spares the user hunting through a list of every open window.
        preferCurrentTab: true,
      } as DisplayMediaStreamOptions);
    } catch (err) {
      // NotAllowedError is the user dismissing the picker or declining.
      if (err instanceof DOMException && err.name === 'NotAllowedError') return null;
      throw err;
    }

    try {
      return await this.grabFrame(stream);
    } finally {
      // Always release the capture, or the browser leaves a "sharing your
      // screen" indicator up after the dialog has closed.
      stream.getTracks().forEach(track => track.stop());
    }
  }

  /** Draw the first frame of `stream` onto a canvas and encode it as a PNG. */
  private async grabFrame(stream: MediaStream): Promise<Blob | null> {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();

    // One animation frame after play() resolves: on some browsers the first
    // frame is not yet painted, which captures as a blank image.
    await new Promise(requestAnimationFrame);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context || !canvas.width || !canvas.height) return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    return new Promise<Blob | null>(resolve =>
      canvas.toBlob(blob => resolve(blob), 'image/png'),
    );
  }
}
